import styled from 'styled-components';

import { PageHead } from './Primitives.styles';

/* ------------------------------ Info hub ------------------------------ */

export const InfoPage = styled.div`
	min-height: 100vh;
	background: var(--cream);
	text-align: left;
`;

/* Hub rows reuse FRow but tame the title size for long labels. */
export const InfoRows = styled.div`
	padding-bottom: 100px;

	h3 {
		font-size: clamp(24px, 3.6vw, 54px);
	}
`;

/* ------------------------------- 404 ---------------------------------- */

export const NotFoundPage = styled.div`
	min-height: 100vh;
	background: var(--cream);
	text-align: left;
`;

export const NotFoundWrap = styled.div`
	padding: 170px var(--pad) 140px;
	position: relative;
	overflow: hidden;

	.ghost {
		font-family: var(--fd);
		font-size: clamp(140px, 26vw, 380px);
		line-height: 0.8;
		color: rgba(var(--ink-rgb), 0.07);
		display: block;
		user-select: none;
		pointer-events: none;
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(30px, 3.7vw, 48px);
		display: inline-block;
		transform: rotate(-2deg);
		margin: 26px 0 10px;
	}

	h1 {
		font-size: clamp(40px, 6.4vw, 92px);
		line-height: 0.96;
		max-width: 16ch;
		margin-bottom: 44px;
	}
`;

/* ------------------------------ About us ------------------------------ */

export const AboutPage = styled.div`
	min-height: 100vh;
	background: var(--cream);
	text-align: left;
`;

export const AboutSections = styled.div`
	padding: 10px var(--pad) 100px;
`;

/* Alternating hairline sections: icon on one side, copy on the other. */
export const AboutSec = styled.section<{ $flip?: boolean; $first?: boolean }>`
	display: flex;
	flex-direction: ${({ $flip }) => ($flip ? 'row-reverse' : 'row')};
	align-items: flex-start;
	gap: clamp(26px, 6vw, 90px);
	padding: 56px 0;
	border-top: ${({ $first }) => ($first ? 'none' : '1px solid var(--hair)')};
	${({ $first }) => ($first ? 'padding-top: 24px;' : '')}

	.ic {
		flex-shrink: 0;
		width: clamp(58px, 8vw, 104px);
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--teal-dark);
	}

	.ic svg {
		width: 100%;
		height: auto;
	}

	.ic svg path {
		fill: var(--teal-dark);
	}

	.tx {
		flex: 1;
	}

	.num {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(24px, 2.2vw, 31px);
		display: inline-block;
		transform: rotate(-2deg);
		margin-bottom: 4px;
	}

	h2 {
		font-size: clamp(30px, 4vw, 56px);
		margin-bottom: 16px;
	}

	p {
		color: var(--brown);
		font-size: 17px;
		line-height: 1.7;
		max-width: 760px;
		margin: 0;
	}

	@media (max-width: 720px) {
		flex-direction: column;
	}
`;

/* -------------------------------- FAQ --------------------------------- */

export const FaqRows = styled.div`
	padding-bottom: 100px;
`;

/* Hairline accordion row: Fjalla question + plus/minus circle indicator. */
export const FaqQRow = styled.div`
	border-top: 1px solid var(--hair);
	cursor: pointer;
	transition: background 0.25s;

	&:last-of-type {
		border-bottom: 1px solid var(--hair);
	}

	&:hover {
		background: rgba(var(--ink-rgb), 0.035);
	}

	.q {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: clamp(14px, 3vw, 44px);
		padding: 30px var(--pad);
	}

	h3 {
		font-size: clamp(21px, 2.8vw, 40px);
	}

	.tgl {
		width: 54px;
		height: 54px;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: 50%;
		position: relative;
		flex-shrink: 0;
		transition: 0.3s;
	}

	.tgl::before,
	.tgl::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		width: 18px;
		height: 2px;
		background: var(--ink);
		transform: translate(-50%, -50%);
		transition: 0.3s;
	}

	.tgl::after {
		transform: translate(-50%, -50%) rotate(90deg);
	}

	&.open .tgl {
		background: var(--teal);
		border-color: var(--teal);
	}

	&.open .tgl::before,
	&.open .tgl::after {
		background: var(--teal-deep);
		transform: translate(-50%, -50%);
	}

	.a {
		padding: 0 var(--pad) 36px;
	}

	.a p {
		max-width: 820px;
		font-size: 16.5px;
		line-height: 1.7;
		color: var(--brown);
		margin: 0;
	}

	@media (max-width: 720px) {
		.tgl {
			width: 42px;
			height: 42px;
		}

		.tgl::before,
		.tgl::after {
			width: 14px;
		}
	}
`;

/* --------------------------- Instructions ----------------------------- */
/* /instructions: the FAQ accordion verbatim, with Caveat let in at two
   points — a handwritten kick above the title and a closing hand note at
   the bottom of the game-flow answers. */

export const InstructionsPage = styled.div`
	min-height: 100vh;
	background: var(--cream);
	text-align: left;

	.chapter + .chapter {
		margin-top: 56px;
	}

	/* handwritten chapter label above each themed group of rows */
	.ch {
		display: inline-block;
		font-family: var(--fh);
		font-weight: 600;
		font-size: clamp(25px, 2.3vw, 32px);
		color: var(--gold-ink);
		transform: rotate(-1.4deg);
		margin: 0 0 12px;
		padding: 0 var(--pad);
	}

	.a .hand {
		display: inline-block;
		font-family: var(--fh);
		font-weight: 600;
		font-size: clamp(22px, 1.9vw, 27px);
		color: var(--teal-dark);
		transform: rotate(-1.2deg);
		margin-top: 14px;
	}
`;

export const InstructionsHead = styled(PageHead)`
	/* the kick precedes the h1, so its offset flips below it */
	.hand {
		margin: 0 0 8px;
	}

	h1 em {
		color: var(--teal-dark);
	}
`;

/* --------------------------- Legal pages ------------------------------ */
/* Shared by CookiePolicy, PaymentProviderSecurity, PrivacyPolicy and
   TermsOfUse (their Page/Head CSS is identical). */

export const LegalPage = styled.div`
	min-height: 100vh;
	background: var(--cream);
	text-align: left;
`;

export const LegalHead = styled(PageHead)`
	h1 {
		font-size: clamp(38px, 5.4vw, 76px);
	}
`;

/* Readable long-form legal column. */
export const CookieArticle = styled.article`
	padding: 0 var(--pad) 100px;

	.col {
		max-width: 820px;
	}

	h2 {
		font-size: clamp(25px, 3vw, 40px);
		margin: 46px 0 16px;
	}

	h3 {
		font-family: var(--fd);
		font-weight: 400;
		letter-spacing: 0.2px;
		text-transform: uppercase;
		color: var(--ink);
		font-size: clamp(19px, 2.2vw, 26px);
		margin: 34px 0 10px;
	}

	p {
		color: var(--brown);
		font-size: 16.5px;
		line-height: 1.7;
		margin: 0 0 18px;
	}
`;

/* Desktop cookie table: hairline rows. */
export const CookieTable = styled.div`
	border-top: 1px solid var(--hair);
	margin: 28px 0 40px;

	.row {
		display: grid;
		grid-template-columns: 1.2fr 0.8fr 1.7fr 0.7fr;
		gap: 18px;
		padding: 15px 0;
		border-bottom: 1px solid var(--hair);
		font-size: 15px;
		color: var(--brown);
		line-height: 1.5;
	}

	.row.head {
		font-size: 12.5px;
		font-weight: 700;
		letter-spacing: 1.6px;
		text-transform: uppercase;
		color: var(--mut);
	}

	.row b {
		font-weight: 700;
		color: var(--ink);
		word-break: break-word;
	}
`;

/* Touch-device stacked cookie list. */
export const CookieList = styled.div`
	border-top: 1px solid var(--hair);
	margin: 28px 0 40px;

	.item {
		padding: 18px 0;
		border-bottom: 1px solid var(--hair);
		font-size: 15px;
		color: var(--brown);
		line-height: 1.6;
	}

	.item b {
		display: block;
		font-weight: 700;
		color: var(--ink);
		margin-bottom: 4px;
		word-break: break-word;
	}

	.item span {
		display: block;
	}
`;

/* Readable long-form legal column. */
export const PaymentSecurityArticle = styled.article`
	padding: 0 var(--pad) 100px;

	.col {
		max-width: 820px;
	}

	p {
		color: var(--brown);
		font-size: 16.5px;
		line-height: 1.7;
		margin: 0 0 18px;
	}

	.cards {
		margin-top: 40px;
	}
`;

/* Readable long-form legal column. */
export const PrivacyArticle = styled.article`
	padding: 0 var(--pad) 100px;

	.col {
		max-width: 820px;
	}

	h2 {
		font-size: clamp(25px, 3vw, 40px);
		margin: 46px 0 16px;
	}

	p,
	ul,
	ol {
		color: var(--brown);
		font-size: 16.5px;
		line-height: 1.7;
		margin: 0 0 18px;
	}

	ul,
	ol {
		padding-left: 24px;
	}

	li {
		margin: 6px 0;
	}

	strong {
		font-weight: 700;
		color: var(--ink);
	}
`;

/* Readable long-form legal column. */
export const TermsArticle = styled.article`
	padding: 0 var(--pad) 100px;

	.col {
		max-width: 820px;
	}

	h2 {
		font-size: clamp(25px, 3vw, 40px);
		margin: 46px 0 16px;
	}

	h3 {
		font-family: var(--fd);
		font-weight: 400;
		letter-spacing: 0.2px;
		text-transform: uppercase;
		color: var(--ink);
		font-size: clamp(19px, 2.2vw, 26px);
		margin: 34px 0 10px;
	}

	p,
	ul,
	ol {
		color: var(--brown);
		font-size: 16.5px;
		line-height: 1.7;
		margin: 0 0 18px;
	}

	ul,
	ol {
		padding-left: 24px;
	}

	li {
		margin: 6px 0;
	}

	b,
	strong {
		font-weight: 700;
		color: var(--ink);
	}

	.fine {
		font-size: 13.5px;
		color: var(--mut);
		margin: 0 0 6px;
	}

	.fine:first-of-type {
		margin-top: 56px;
	}
`;
